// formati e frasi comuni a lista, dettaglio e schede delle offerte
import moment from 'moment';
import { formatItemText, nomeCliente } from '../coupon/formato';

export { formatItemText, nomeCliente };

// modalita' dell'offerta, per il toggle della scheda
export const TIPOLOGIE = [
	{ key: 'quantity', value: 'Quantità', label: 'Per quantità', icon: 'mdi-counter' },
	{ key: 'date', value: 'Data inizio / Data fine', label: 'Per date', icon: 'mdi-calendar-range' },
	{ key: 'time', value: 'A tempo', label: 'A tempo', icon: 'mdi-timer-outline' },
	{ key: 'bundle', value: 'Bundle', label: 'Bundle', icon: 'mdi-package-variant-plus' },
];

// indirizzo della pagina pubblica del bundle: "Più acquisti, più risparmi" -> "piu-acquisti-piu-risparmi"
export function slugify(testo) {
	return String(testo || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}

const numero = v => {
	const n = Number(v);
	return v === '' || v === null || v === undefined || isNaN(n) ? null : n;
};

export function euro(v) {
	const n = numero(v);
	return n === null ? '—' : `€ ${n.toFixed(2).replace('.', ',')}`;
}

// "−10%" oppure "−€ 5,00"; per il bundle "2° −10% · 3° −20%"
export function sconto(o) {
	if (o && o.operation === 'bundle') {
		const secondo = numero(o.bundle && o.bundle.second_discount);
		const terzo = numero(o.bundle && o.bundle.third_discount);
		const parti = [];
		if (secondo) parti.push(`2° −${String(secondo).replace('.', ',')}%`);
		if (terzo) parti.push(`3° −${String(terzo).replace('.', ',')}%`);
		return parti.length ? parti.join(' · ') : '—';
	}
	const n = numero(o && o.discount);
	if (!n) return '—';
	return o.typology === 'percent' ? `−${String(n).replace('.', ',')}%` : `−${euro(n)}`;
}

// stesso calcolo del sito (HandlerOffer.applyDicount): sconto e arrotondamento per difetto al centesimo
export function prezzoScontato(o, prezzo) {
	const n = numero(o && o.discount) || 0;
	let p = o.typology === 'percent' ? prezzo - (prezzo * n) / 100 : prezzo - n;
	p = Math.floor(p * 100) / 100;
	return p;
}

const data = d => (d ? moment(d).format('DD/MM/YYYY') : '');

// stato di un'offerta a date rispetto a oggi
export function statoDate(o) {
	if (!o.start_at && !o.finish_at) return { testo: 'senza date', colore: 'grey' };
	const ora = moment();
	if (o.start_at && moment(o.start_at).isAfter(ora)) return { testo: 'non ancora iniziata', colore: 'info' };
	if (o.finish_at && moment(o.finish_at).isBefore(ora)) return { testo: 'scaduta', colore: 'grey' };
	return { testo: 'in corso', colore: 'success' };
}

export function periodo(o) {
	// a tempo: vale da subito, conta anche l'ora di fine
	if (o.operation === 'time') return o.finish_at ? `fino al ${data(o.finish_at)} alle ${moment(o.finish_at).format('HH:mm')}` : 'fine non impostata';
	if (o.start_at && o.finish_at) return `dal ${data(o.start_at)} al ${data(o.finish_at)}`;
	if (o.start_at) return `dal ${data(o.start_at)}`;
	if (o.finish_at) return `fino al ${data(o.finish_at)}`;
	return 'date non impostate';
}

// stesso calcolo del sito (features/offers/bundle.js): il più caro pieno, poi 2° e 3° scontati, arrotondati al centesimo
export function prezziBundle(o, prezzi) {
	const perc = [0, numero(o && o.bundle && o.bundle.second_discount) || 0, numero(o && o.bundle && o.bundle.third_discount) || 0];
	return [...prezzi]
		.sort((a, b) => b - a)
		.map((prezzo, i) => ({ prima: prezzo, dopo: Math.max(0, Math.round((prezzo - (prezzo * perc[i]) / 100) * 100) / 100), sconto: perc[i] }));
}

// riepilogo della modalita': date o quantita'
export function modalita(o) {
	if (o.operation === 'bundle') {
		const venduti = numero(o.progress) || 0;
		const quando = o.start_at || o.finish_at ? periodo(o) : 'sempre';
		return `${quando} · ${venduti} ${venduti === 1 ? 'bundle venduto' : 'bundle venduti'}`;
	}
	if (o.operation === 'time' && numero(o.quantity) > 0) return `${periodo(o)} · ${numero(o.progress) || 0} su ${numero(o.quantity)} pezzi venduti`;
	if (o.operation === 'date' || o.operation === 'time') return periodo(o);
	if (o.operation === 'quantity') return `${numero(o.progress) || 0} su ${numero(o.quantity) || 0} usate`;
	return 'sempre';
}

// parti del catalogo coperte: ["3 prodotti", "1 brand", ...]
function partiCatalogo(o) {
	const parti = [];
	const n = (k, uno, tanti) => {
		const c = (o[k] || []).length;
		if (c) parti.push(`${c} ${c === 1 ? uno : tanti}`);
	};
	n('items', 'prodotto', 'prodotti');
	n('brands', 'brand', 'brand');
	n('categories', 'categoria', 'categorie');
	return parti;
}

// "3 prodotti · 1 brand · 2 categorie" oppure "tutto il catalogo"
export function ambito(o) {
	const parti = partiCatalogo(o);
	const soglia = o.operation === 'bundle' && numero(o.min_price) ? ` sopra ${euro(o.min_price)}` : '';
	const catalogo = (parti.length ? parti.join(' · ') : 'tutto il catalogo') + soglia;
	const clienti = (o.customers || []).length;
	return clienti ? `${catalogo} · ${clienti} ${clienti === 1 ? 'cliente' : 'clienti'}` : catalogo;
}

// l'offerta in una frase
export function frase(o) {
	if (o.operation === 'bundle') {
		const parti = partiCatalogo(o);
		const dove = parti.length ? `fra ${parti.join(', ')}` : 'fra tutti i prodotti del catalogo';
		const soglia = numero(o.min_price) ? ` sopra ${euro(o.min_price)}` : '';
		const quando = o.start_at || o.finish_at ? `, ${periodo(o)}` : '';
		return `Il cliente compone sul sito un bundle di 2 o 3 prodotti ${dove}${soglia}: ${sconto(o)} sui meno cari${quando}.`;
	}
	const cosa = sconto(o) === '—' ? 'uno sconto' : `uno sconto di ${sconto(o)}`;
	const parti = partiCatalogo(o);
	const dove = parti.length ? `su ${parti.join(', ')}` : 'su tutto il catalogo';
	const chi = (o.customers || []).length ? ', solo per i clienti scelti' : '';
	const soglia = numero(o.min_price) ? `, solo con prezzo sopra ${euro(o.min_price)}` : '';
	let quando = '';
	if (o.operation === 'date' || o.operation === 'time') quando = `, ${periodo(o)}`;
	if (o.operation === 'quantity') quando = `, finché non se ne usano ${numero(o.quantity) || 0}`;
	return `I prezzi mostrano ${cosa} ${dove}${soglia}${chi}${quando}.`;
}
